import React, { useState } from "react";
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Box,
  Divider,
  Paper,
  List,
  ListItem,
  IconButton,
  ListItemIcon,
  ListItemText,
  Chip,
} from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { getAccessToken } from "../../../services/authenticationService";
import { OutletApprovalDocument } from "../../../types/types";
import { openPopup } from "../../../stateManagement/actions/popupActions";
import { GenericModal, getGenericModalProps } from "../../viewOrder/ViewOrderUtils";
import { getReport } from "../../../services/reportService";
import { ChannelkartNetworkGet } from "../../../services/networkService";

interface OutletDetailsProps {
  entityId: string;
  outletCode: string;
  outletName: string;
  city: string;
  contactNumber: string;
  email: string;
  outletType: string;
  documents?: OutletApprovalDocument[];
  status: string;
  onDownloadReport?: (outletCode: string, entityId: string) => void;
  onReject?: () => void;
  documentDownloadFails?: (modal: GenericModal) => void;
  referenceId: string;
  downloadingReport: boolean
}

const OutletDetails: React.FC<OutletDetailsProps> = ({
  entityId,
  outletCode,
  outletName,
  city,
  contactNumber,
  email,
  outletType,
  documents,
  status,
  onDownloadReport,
  onReject,
  documentDownloadFails,
  referenceId,
  downloadingReport
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  status = status.toLowerCase();
  const normalDocs = documents?.filter((d) => d.category !== "AR");
  const arDocs = documents?.filter((d) => d.category === "AR");

  const downloadDocument = async (urls: string[], fileName: string) => {
    const token = getAccessToken();

    if (isLoading){
      openPopup("Alert", "Wait! Download in Progress");
      return;
    }
    setIsLoading(true);

    try {
      for (let i = 0; i < urls.length; i++) {
        const url = urls[i];

        const response = await fetch(url, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error(`Failed to download ${url}`);
        }

        const blob = await response.blob();
        const cd = response.headers.get("Content-Disposition");
        let extractedExtension = "jpg"; 

        if (cd) {
          const match = cd.match(/filename\*?=['"]?([^'";]+)['"]?/i);

          if (match) {
            const fullHeaderFilename = match[1];           
            const splitted = fullHeaderFilename.split("."); 
            const ext = splitted[splitted.length - 1];

            if (ext) extractedExtension = ext;
          }
        }
        const finalName = `${fileName}.${extractedExtension}`;

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = finalName;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }

    } catch (err) {

      documentDownloadFails?.(
        getGenericModalProps("error", "Error in downloading Document")
      );
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <Box
      sx={{
        width: "100%",
        mx: "auto",
        height: "90%",
        textAlign: "left",
        border: "none",
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12,
        borderBottom: "1px solid #efefef",
        borderLeft: "1px solid #efefef",
        mb: 3
      }}
    >

      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 2,
            py: 1,
            bgcolor: "white",
          }}
        >
          <Card variant="outlined" sx={{ mb: 3, borderRadius: 3, width: "70%" }}>
            <Box
              sx={{
                borderBottom: "1px solid #e0e0e0",
                py: 1.5,
                px: 2.5,
                backgroundColor: "#f5f9fb",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h6"
                fontWeight={500}
                color="#33708c"
                fontSize={16}
              >
                Outlet Details
              </Typography>

              <Chip
                label={status.toUpperCase() ?? "N/A"}
                sx={{
                  height: 24,
                  fontSize: 12,
                  fontWeight: 500,
                  backgroundColor:
                    status === "approved"
                      ? "#d6f5dd"
                      : status === "rejected"
                        ? "#fde0e0"
                        : "#f9ebc5",
                  color:
                    status === "approved"
                      ? "#1b5e20"
                      : status === "rejected"
                        ? "#b71c1c"
                        : "#cb911f",
                  textTransform: "capitalize",
                  marginLeft: 2
                }}
              />
            </Box>


            <CardContent>
              <Grid container spacing={3}>
                {/* Outlet Name */}
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Paper
                      elevation={0}
                      sx={{
                        backgroundColor: "#fef9c9",
                        width: 30,
                        height: 30,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 2,
                      }}
                    >
                      <StorefrontOutlinedIcon sx={{ color: "#c6963b", fontSize: 18 }} />
                    </Paper>
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontSize={12}>
                        Outlet Name
                      </Typography>
                      <Typography fontWeight={500} fontSize={14}>{outletName}</Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* City */}
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Paper
                      elevation={0}
                      sx={{
                        backgroundColor: "#fef9c9",
                        width: 30,
                        height: 30,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 2,
                      }}
                    >
                      <LocationOnOutlinedIcon sx={{ color: "#c6963b", fontSize: 18 }} />
                    </Paper>
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontSize={12}>
                        City
                      </Typography>
                      <Typography fontWeight={500} fontSize={14}>{city}</Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Contact Number */}
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Paper
                      elevation={0}
                      sx={{
                        backgroundColor: "#fef9c9",
                        width: 30,
                        height: 30,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 2,
                      }}
                    >
                      <PhoneOutlinedIcon sx={{ color: "#c6963b", fontSize: 18 }} />
                    </Paper>
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontSize={12}>
                        Mobile Number
                      </Typography>
                      <Typography fontWeight={500} fontSize={14}>{contactNumber}</Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Email */}
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Paper
                      elevation={0}
                      sx={{
                        backgroundColor: "#fef9c9",
                        width: 30,
                        height: 30,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 2,
                      }}
                    >
                      <MailOutlineIcon sx={{ color: "#c6963b", fontSize: 18 }} />
                    </Paper>

                    <Box>
                      <Typography variant="body2" color="text.secondary" fontSize={12}>
                        Email
                      </Typography>
                      <Typography fontWeight={500} fontSize={14}>{email}</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* All Outlet Details Section */}
          <Card variant="outlined" sx={{ mb: 3, borderRadius: 3, width: "70%" }}>
            <Box sx={{ borderBottom: "1px solid #e0e0e0", py: 1.5, px: 2.5, backgroundColor: "#f5f9fb" }}>
              <Typography variant="h6" fontWeight={500} color="#33708c" fontSize={16}>
                All Outlet Details
              </Typography>
            </Box>

            <CardContent>
              <Box
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  backgroundColor: "#f3fdf5",
                  borderRadius: 2,
                  border: "1px solid #d1f8dc",
                  p: 1.5,
                  gap: 2,
                }}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: "#e2fbe9",
                      width: 40,
                      height: 40,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 2,
                    }}
                  >
                    <DescriptionOutlinedIcon sx={{ color: "#387f3d" }} />
                  </Paper>
                  <Box>
                    <Typography fontWeight={500} fontSize={14}>All Outlet Details</Typography>
                    <Typography variant="body2" color="text.secondary" fontSize={12}>
                      Excel file containing complete outlet information
                    </Typography>
                  </Box>
                </Box>

                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<DownloadIcon />}
                  sx={{
                    textTransform: "none",
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    fontWeight: 600,
                    backgroundColor: "white"
                  }}
                  disabled={downloadingReport}
                  onClick={() => onDownloadReport?.(outletCode, entityId)}
                >
                  {downloadingReport ? "Downloading..." : "Download Excel"}
                </Button>

              </Box>
            </CardContent>
          </Card>


          {outletType.toLowerCase() !== "existing" && (documents?.length || 0) > 0 && <Card variant="outlined" sx={{ mb: 3, borderRadius: 3, width: "70%" }}>
            <Box sx={{ borderBottom: "1px solid #e0e0e0", py: 1.5, px: 2.5, backgroundColor: "#f5f9fb" }}>
              <Typography variant="h6" fontWeight={500} color="#33708c" fontSize={16}>
                Documents
              </Typography>
            </Box>

            <CardContent sx={{ py: 1.5, px: 1.5 }}>
              {/* Main Document List */}
              <List disablePadding>
                {normalDocs?.map((doc, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      px: 2,
                      py: 1.2,
                      "&:hover": { backgroundColor: "#f5f9fb" },
                      borderRadius: 2
                    }}
                    secondaryAction={
                      <IconButton edge="end" color="primary" onClick={() => downloadDocument(doc.url, doc.name)}>
                        <DownloadIcon />
                      </IconButton>
                    }
                  >
                    <ListItemIcon>
                      <Paper
                        elevation={0}
                        sx={{
                          backgroundColor: "#e4f2fd",
                          width: 30,
                          height: 30,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: 2,
                        }}
                      >
                        <DescriptionOutlinedIcon sx={{ color: "#33728e", fontSize: 18 }} />
                      </Paper>
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{
                        fontSize: 15,
                        color: "#333",
                      }}
                      primary={doc.name}
                    />
                  </ListItem>
                ))}
              </List>

              {/* AR Customer Section */}
              {arDocs && arDocs.length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography
                    sx={{ fontWeight: 600, color: "#546e7a", px: 3, mb: 1 }}
                  >
                    AR Customer Document
                  </Typography>
                  <List disablePadding>
                    {arDocs.map((doc, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          px: 2,
                          py: 1.2,
                          "&:hover": { backgroundColor: "#f5f9fb" },
                          borderRadius: 2
                        }}
                        secondaryAction={
                          <IconButton edge="end" color="primary" onClick={() => downloadDocument(doc.url, doc.name)}>
                            <DownloadIcon />
                          </IconButton>
                        }
                      >
                        <ListItemIcon>
                          <Paper
                            elevation={0}
                            sx={{
                              backgroundColor: "#e4f2fd",
                              width: 30,
                              height: 30,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: 2,
                            }}
                          >
                            <DescriptionOutlinedIcon sx={{ color: "#33728e", fontSize: 18 }} />
                          </Paper>
                        </ListItemIcon>
                        <ListItemText
                          primaryTypographyProps={{
                            fontSize: 15,
                            color: "#333",
                          }}
                          primary={doc.name}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </CardContent>
          </Card>}
        </Box>

        {/* Reject Button */}
        {status === "pending" && outletType.toLowerCase() !== "existing" && <Box
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 2,
            backgroundColor: "#fff",
            borderRight: "1px solid #efefef",
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
            py: 1.5,
            px: 3,
            display: "flex",
            justifyContent: "flex-end",
            boxShadow: "0 -4px 8px rgba(17, 17, 17, 0.08)",
          }}
        >
          <Button
            variant="contained"
            color="error"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              py: 1,
            }}
            onClick={onReject}
          >
            Reject
          </Button>
        </Box>}

      </Box>

    </Box>
  );
};

export default OutletDetails;
